import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { UserRole } from '../../../../../types';
import { useForm } from 'react-hook-form';

import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { UserKeys, updateRole } from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  BillingRoles,
  CalendarRoles,
  DirectoryRoles,
  DriveRoles,
  GPanelContactsPermissions,
  isAbleToSeeUIElement,
  RulesEngineRoles,
  ToolsRoles,
  UserRoles,
  UserViewRoles,
  VaultRoles,
} from '../../../../utils/roles/client-roles';
import RoleAccordianSection from './role-accordian-section';
import { Messages } from '../../../../utils/message-constants';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { getVisibleDriveRoles } from '../../../../utils/roles/visible-drive-roles';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import useDebounce from '../../../../utils/useDebounce';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import UnfoldLess from '@mui/icons-material/UnfoldLess';

const visibleToolsRoles = Object.fromEntries(
  Object.entries(ToolsRoles).filter(
    ([key]) => key !== 'RunExportAdminLogsReport'
  )
) as typeof ToolsRoles;

interface RolePermissionsFormProps {
  changeLogRoles: string[];
  role: UserRole;
}

export default function RolePermissionsForm({
  changeLogRoles,
  role,
}: RolePermissionsFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const { activeLabs, features } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedPermissionType, setSelectedPermissionType] = useState('all');
  const [openCloseAccordians, setOpenCloseAccordians] = useState(true);
  const queryClient = useQueryClient();
  const isGpanelContactsFeatureEnabled = activeLabs.includes('gpanelcontacts');
  const isDriveLabelsFeatureEnabled = isFeatureEnabled(
    'driveLabelsRead',
    features
  );
  const isSlackIntegrationFeatureEnabled = isFeatureEnabled(
    'slackIntegration',
    features
  );
  const isGroupsGovernanceFeatureEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );

  const visibleDirectoryRoles = isGroupsGovernanceFeatureEnabled
    ? DirectoryRoles
    : (Object.fromEntries(
        Object.entries(DirectoryRoles).filter(
          ([key]) => key !== 'EditGroupHealthSettings'
        )
      ) as typeof DirectoryRoles);
  const isVaultExportFeatureEnabled = isFeatureEnabled(
    'vaultExportReact',
    features
  );
  const isUserRolesTabFeatureEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );

  const visibleUserRoles = isUserRolesTabFeatureEnabled
    ? UserRoles
    : (Object.fromEntries(
        Object.entries(UserRoles).filter(([key]) => key !== 'ViewUserRolesTab')
      ) as typeof UserRoles);

  const visibleDriveRoles = getVisibleDriveRoles(features);

  let visibleAdministrationRoles = isDriveLabelsFeatureEnabled
    ? AdministrationRoles
    : (Object.fromEntries(
        Object.entries(AdministrationRoles).filter(
          ([key]) => key !== 'ViewLabelsManager'
        )
      ) as typeof AdministrationRoles);

  if (!isSlackIntegrationFeatureEnabled) {
    visibleAdministrationRoles = Object.fromEntries(
      Object.entries(visibleAdministrationRoles).filter(
        ([key]) =>
          !['ViewSlackIntegration', 'UpdateSlackIntegration'].includes(key)
      )
    ) as typeof AdministrationRoles;
  }

  const allRolesArray: {
    name: string;
    value: string;
    type: string;
    group: string;
    hasChangeLog: boolean;
  }[] = [];
  Object.keys(visibleDriveRoles).forEach((key) =>
    allRolesArray.push(visibleDriveRoles[key as keyof typeof visibleDriveRoles])
  );
  Object.keys(visibleAdministrationRoles).forEach((key) =>
    allRolesArray.push(
      visibleAdministrationRoles[key as keyof typeof visibleAdministrationRoles]
    )
  );
  Object.keys(CalendarRoles).forEach((key) =>
    allRolesArray.push(CalendarRoles[key as keyof typeof CalendarRoles])
  );
  Object.keys(RulesEngineRoles).forEach((key) =>
    allRolesArray.push(RulesEngineRoles[key as keyof typeof RulesEngineRoles])
  );
  Object.keys(visibleDirectoryRoles).forEach((key) => {
    allRolesArray.push(
      visibleDirectoryRoles[key as keyof typeof visibleDirectoryRoles]
    );
  });

  if (isGpanelContactsFeatureEnabled) {
    Object.keys(GPanelContactsPermissions).forEach((key) =>
      allRolesArray.push(
        GPanelContactsPermissions[key as keyof typeof GPanelContactsPermissions]
      )
    );
  }
  Object.keys(visibleToolsRoles).forEach((key) =>
    allRolesArray.push(visibleToolsRoles[key as keyof typeof visibleToolsRoles])
  );
  if (isVaultExportFeatureEnabled) {
    Object.keys(VaultRoles).forEach((key) =>
      allRolesArray.push(VaultRoles[key as keyof typeof VaultRoles])
    );
  }
  Object.keys(visibleUserRoles).forEach((key) =>
    allRolesArray.push(visibleUserRoles[key as keyof typeof visibleUserRoles])
  );
  Object.keys(BillingRoles).forEach((key) =>
    allRolesArray.push(BillingRoles[key as keyof typeof BillingRoles])
  );
  Object.keys(UserViewRoles).forEach((key) =>
    allRolesArray.push(UserViewRoles[key as keyof typeof UserViewRoles])
  );
  const userViewRolesValues = useMemo(
    () => Object.values(UserViewRoles).map((role) => role.value),
    []
  );
  const directoryRolesValues = Object.values(visibleDirectoryRoles).map(
    (role) => role.value
  );

  const gPanelContactsRolesValues = useMemo(
    () => Object.values(GPanelContactsPermissions).map((role) => role.value),
    []
  );
  const toolsRolesValues = useMemo(
    () => Object.values(visibleToolsRoles).map((role) => role.value),
    []
  );
  const vaultRolesValues = useMemo(
    () =>
      isVaultExportFeatureEnabled
        ? Object.values(VaultRoles).map((role) => role.value)
        : [],
    [isVaultExportFeatureEnabled]
  );
  const toolsAndVaultRoleValues = useMemo(
    () => [...toolsRolesValues, ...vaultRolesValues],
    [toolsRolesValues, vaultRolesValues]
  );
  const driveRolesValues = useMemo(
    () => Object.values(DriveRoles).map((role) => role.value),
    []
  );
  const administrationRolesValues = useMemo(
    () => Object.values(visibleAdministrationRoles).map((role) => role.value),
    [visibleAdministrationRoles]
  );
  const calendarRolesValues = useMemo(
    () => Object.values(CalendarRoles).map((role) => role.value),
    []
  );
  const rulesEngineRolesValues = useMemo(
    () => Object.values(RulesEngineRoles).map((role) => role.value),
    []
  );
  const billingRolesValues = useMemo(
    () => Object.values(BillingRoles).map((role) => role.value),
    []
  );
  const userRolesValues = useMemo(
    () => Object.values(visibleUserRoles).map((role) => role.value),
    [visibleUserRoles]
  );
  const immediateNotificationStarValues = [
    ...directoryRolesValues,
    ...toolsRolesValues,
    ...vaultRolesValues,
    ...driveRolesValues,
    ...administrationRolesValues,
    ...calendarRolesValues,
    ...rulesEngineRolesValues,
    ...userRolesValues,
    ...userViewRolesValues,
    ...billingRolesValues,
  ];
  const changeLogStarRoles = allRolesArray.filter((role) => role.hasChangeLog);
  const changeLogStarValues = changeLogStarRoles.map((role) => role.value);

  const { mutate, isPending: updatingRole } = useMutation({
    mutationFn: (data: UserRole) => {
      const dataToolsRoles = data.toolsRoles || [];

      const updatedRole: UserRole = {
        ...data,
        userRoles:
          data.userRoles!.length === userRolesValues.length
            ? ['*']
            : data.userRoles,
        userViewRoles:
          data.userViewRoles!.length === userViewRolesValues.length
            ? ['*']
            : data.userViewRoles,
        directoryRoles:
          data.directoryRoles!.length === directoryRolesValues.length
            ? ['*']
            : data.directoryRoles,
        gPanelContactsRoles:
          data.gPanelContactsRoles!.length === gPanelContactsRolesValues.length
            ? ['*']
            : data.gPanelContactsRoles,
        calendarRoles:
          data.calendarRoles!.length === calendarRolesValues.length
            ? ['*']
            : data.calendarRoles,
        driveRoles:
          data.driveRoles!.length === driveRolesValues.length
            ? ['*']
            : data.driveRoles,
        toolsRoles:
          dataToolsRoles.length === toolsAndVaultRoleValues.length
            ? ['*']
            : dataToolsRoles,
        administrationRoles:
          data.administrationRoles!.length === administrationRolesValues.length
            ? ['*']
            : data.administrationRoles,
        billingRoles:
          data.billingRoles!.length === billingRolesValues.length
            ? ['*']
            : data.billingRoles,
        rulesEngineRoles:
          data.rulesEngineRoles!.length === rulesEngineRolesValues.length
            ? ['*']
            : data.rulesEngineRoles,
        immediateNotification:
          data.immediateNotification!.length === allRolesArray.length
            ? ['*']
            : data.immediateNotification,
        changeLogs:
          data.changeLogs!.length === changeLogStarValues.length
            ? ['*']
            : data.changeLogs,
      };
      return updateRole(updatedRole, changeLogMessage);
    },
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Role permissions'),
      });

      queryClient.setQueryData([UserKeys.Roles], (oldData: UserRole[]) => {
        if (!oldData) {
          return;
        } else {
          const filteredList = oldData.filter(
            (currentRole) => currentRole.roleName !== role?.roleName
          );
          return [...filteredList, data];
        }
      });
      reset({
        ...data,
        userViewRoles: !data.userViewRoles
          ? []
          : data.userViewRoles.includes('*')
          ? userViewRolesValues
          : data.userViewRoles,
        userRoles: !data.userRoles
          ? []
          : data.userRoles.includes('*')
          ? userRolesValues
          : data.userRoles,
        directoryRoles: !data.directoryRoles
          ? []
          : data.directoryRoles.includes('*')
          ? directoryRolesValues
          : data.directoryRoles,
        gPanelContactsRoles: !data.gPanelContactsRoles
          ? []
          : data.gPanelContactsRoles.includes('*')
          ? gPanelContactsRolesValues
          : data.gPanelContactsRoles,
        calendarRoles: !data.calendarRoles
          ? []
          : data.calendarRoles.includes('*')
          ? calendarRolesValues
          : data.calendarRoles,
        driveRoles: !data.driveRoles
          ? []
          : data.driveRoles.includes('*')
          ? driveRolesValues
          : data.driveRoles,
        toolsRoles: !data.toolsRoles
          ? []
          : data.toolsRoles.includes('*')
          ? toolsAndVaultRoleValues
          : data.toolsRoles,
        administrationRoles: !data.administrationRoles
          ? []
          : data.administrationRoles.includes('*')
          ? administrationRolesValues
          : data.administrationRoles,
        billingRoles: !data.billingRoles
          ? []
          : data.billingRoles.includes('*')
          ? billingRolesValues
          : data.billingRoles,
        rulesEngineRoles: !data.rulesEngineRoles
          ? []
          : data.rulesEngineRoles.includes('*')
          ? rulesEngineRolesValues
          : data.rulesEngineRoles,
        immediateNotification: !data.immediateNotification
          ? []
          : data.immediateNotification.includes('*')
          ? immediateNotificationStarValues
          : data.immediateNotification,
        changeLogs: !data.changeLogs
          ? []
          : data.changeLogs.includes('*')
          ? changeLogStarValues
          : data.changeLogs,
      });
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the role permissions'
        ),
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(
        analytics.administration.role_details.update_role_permissions,
        {
          module: ModuleList.modules.administration.name,
          section: ModuleList.modules.administration.sections.roleManager,
          roleType: data.roleType,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isLoading },
    getValues,
    setValue,
    watch,
    reset,
  } = useForm<UserRole>({
    defaultValues: {
      ...role,
      userViewRoles: !role.userViewRoles
        ? []
        : role.userViewRoles.includes('*')
        ? userViewRolesValues
        : role.userViewRoles,
      userRoles: !role.userRoles
        ? []
        : role.userRoles.includes('*')
        ? userRolesValues
        : role.userRoles,
      directoryRoles: !role.directoryRoles
        ? []
        : role.directoryRoles.includes('*')
        ? directoryRolesValues
        : role.directoryRoles,
      gPanelContactsRoles: !role.gPanelContactsRoles
        ? []
        : role.gPanelContactsRoles.includes('*')
        ? gPanelContactsRolesValues
        : role.gPanelContactsRoles,
      calendarRoles: !role.calendarRoles
        ? []
        : role.calendarRoles.includes('*')
        ? calendarRolesValues
        : role.calendarRoles,
      driveRoles: !role.driveRoles
        ? []
        : role.driveRoles.includes('*')
        ? driveRolesValues
        : role.driveRoles,
      toolsRoles: !role.toolsRoles
        ? []
        : role.toolsRoles.includes('*')
        ? toolsAndVaultRoleValues
        : role.toolsRoles,
      administrationRoles: !role.administrationRoles
        ? []
        : role.administrationRoles.includes('*')
        ? administrationRolesValues
        : role.administrationRoles,
      billingRoles: !role.billingRoles
        ? []
        : role.billingRoles.includes('*')
        ? billingRolesValues
        : role.billingRoles,
      rulesEngineRoles: !role.rulesEngineRoles
        ? []
        : role.rulesEngineRoles.includes('*')
        ? rulesEngineRolesValues
        : role.rulesEngineRoles,
      immediateNotification: !role.immediateNotification
        ? []
        : role.immediateNotification.includes('*')
        ? immediateNotificationStarValues
        : role.immediateNotification,
      changeLogs: !role.changeLogs
        ? []
        : role.changeLogs.includes('*')
        ? changeLogStarValues
        : role.changeLogs,
    },
  });

  const { showDialog, handleConfirm, handleCancel, isCancelAction } =
    useUnsavedChanges(isDirty);

  const onSubmit = (data: UserRole) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, AdministrationRoles.EditRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const userViewRoles = watch('userViewRoles') || [];
  const userRoles = watch('userRoles') || [];
  const immediateNotifications = watch('immediateNotification') || [];
  const changeLogs = watch('changeLogs') || [];
  const directoryRoles = watch('directoryRoles') || [];
  const gPanelContactsRoles = watch('gPanelContactsRoles') || [];
  const calendarRoles = watch('calendarRoles') || [];
  const driveRoles = watch('driveRoles') || [];
  const toolsRoles = watch('toolsRoles') || [];
  const administrationRoles = watch('administrationRoles') || [];
  const billingRoles = watch('billingRoles') || [];
  const rulesEngineRoles = watch('rulesEngineRoles') || [];
  const permissionTypeOptions = [
    { displayValue: 'All', value: 'all' },
    { displayValue: 'Create', value: 'create' },
    { displayValue: 'Delete', value: 'delete' },
    { displayValue: 'Trash', value: 'trash' },
    { displayValue: 'Edit', value: 'update' },
    { displayValue: 'View', value: 'view' },
  ];

  return (
    <Box
      component="form"
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{ position: 'absolute', top: -50, right: -15 }}
      >
        <Button
          variant="contained"
          disabled={!isDirty || updatingRole}
          loading={updatingRole || isLoading}
          sx={{ position: 'absolute', top: 0, right: 0 }}
          type="submit"
        >
          Save
        </Button>
      </Stack>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack flexDirection={'row'} gap={3}>
              <Box sx={{ width: 400 }}>
                <TextField
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search for a permission..."
                  fullWidth
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setSearchTerm('')}
                          edge="end"
                          size="small"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
              <Divider orientation="vertical" flexItem />
              <TextField
                inputProps={{
                  id: 'permissionType',
                }}
                InputLabelProps={{
                  htmlFor: 'permissionType',
                }}
                select
                label="Permission Type"
                value={selectedPermissionType}
                onChange={(e) => setSelectedPermissionType(e.target.value)}
                sx={{ minWidth: 130 }}
              >
                {permissionTypeOptions.map((permission) => (
                  <MenuItem key={permission.value} value={permission.value}>
                    {permission.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Tooltip arrow title="Collapse/Expand All">
              <IconButton
                onClick={() => setOpenCloseAccordians(!openCloseAccordians)}
              >
                <UnfoldLess />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
      <Box
        sx={{
          flex: 1,
          height: '100%',
          overflowY: 'auto',
          pb: 1,
        }}
      >
        {role.roleType === 'user' ? (
          <Stack gap={2}>
            <RoleAccordianSection
              controlName={'userViewRoles'}
              title={'User View Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={userViewRoles}
              allPermissionsValues={userViewRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={UserViewRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
          </Stack>
        ) : (
          <Stack gap={2} mb={2}>
            <RoleAccordianSection
              controlName={'userRoles'}
              title={'User Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={userRoles}
              allPermissionsValues={userRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={visibleUserRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'directoryRoles'}
              title={'Directory Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={directoryRoles}
              allPermissionsValues={directoryRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={visibleDirectoryRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            {isGpanelContactsFeatureEnabled && (
              <RoleAccordianSection
                controlName={'gPanelContactsRoles'}
                title={'gPanel Contacts Permissions'}
                setValue={setValue}
                role={role}
                formPermissionsValues={gPanelContactsRoles}
                allPermissionsValues={gPanelContactsRolesValues}
                immediateNotifications={immediateNotifications}
                changeLogs={changeLogs}
                roleType={GPanelContactsPermissions}
                searchTerm={debouncedSearchTerm}
                selectedPermissionType={selectedPermissionType}
                openCloseAllAccordians={openCloseAccordians}
              />
            )}
            <RoleAccordianSection
              controlName={'calendarRoles'}
              title={'Calendar Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={calendarRoles}
              allPermissionsValues={calendarRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={CalendarRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'driveRoles'}
              title={'Drive Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={driveRoles}
              allPermissionsValues={driveRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={visibleDriveRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            {isVaultExportFeatureEnabled && (
              <RoleAccordianSection
                controlName={'toolsRoles'}
                title={'Vault Permissions'}
                setValue={setValue}
                role={role}
                formPermissionsValues={toolsRoles}
                allPermissionsValues={vaultRolesValues}
                immediateNotifications={immediateNotifications}
                changeLogs={changeLogs}
                roleType={VaultRoles}
                searchTerm={debouncedSearchTerm}
                selectedPermissionType={selectedPermissionType}
                openCloseAllAccordians={openCloseAccordians}
              />
            )}
            <RoleAccordianSection
              controlName={'toolsRoles'}
              title={'Tools Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={toolsRoles}
              allPermissionsValues={toolsRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={visibleToolsRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'administrationRoles'}
              title={'Administration Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={administrationRoles}
              allPermissionsValues={administrationRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={visibleAdministrationRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'billingRoles'}
              title={'Billing Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={billingRoles}
              allPermissionsValues={billingRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={BillingRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'rulesEngineRoles'}
              title={'Rules Engine Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={rulesEngineRoles}
              allPermissionsValues={rulesEngineRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={RulesEngineRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
            <RoleAccordianSection
              controlName={'userViewRoles'}
              title={'User View Permissions'}
              setValue={setValue}
              role={role}
              formPermissionsValues={userViewRoles}
              allPermissionsValues={userViewRolesValues}
              immediateNotifications={immediateNotifications}
              changeLogs={changeLogs}
              roleType={UserViewRoles}
              searchTerm={debouncedSearchTerm}
              selectedPermissionType={selectedPermissionType}
              openCloseAllAccordians={openCloseAccordians}
            />
          </Stack>
        )}
      </Box>
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        pageMode="edit"
        entityName="Role"
        open={showDialog}
        handleConfirm={handleConfirm}
        handleClose={handleCancel}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const data: UserRole = {
            ...getValues(),
          };
          mutate(data);
        }}
        mutateLoading={updatingRole}
      />
    </Box>
  );
}
